<script setup lang="ts">
import { ManyProgrammesByTypeQuery } from '~/queries/many/programme-types'

const props = defineProps<{
  block: BlockEvents,
  noHorizontalPadding?: boolean,
}>()

const { locale } = useI18n()

interface Recurrence {
  id?: string | null
  dateTimeStart: string
  dateTimeEnd: string
}

interface ProgrammeEntry {
  id: string
  title: string
  slug: string
  type: { slug: string, type?: string }
  recurrences: Recurrence[]
  content?: unknown[]
}

interface ProgrammesResponse {
  docs: ProgrammeEntry[]
  totalDocs: number
}

const typeIds = computed(() =>
  props.block.programmeTypes?.map((t: any) => t.id) ?? []
)

const { data } = await useAsyncData<ProgrammesResponse>(
  () => `block-events-${props.block.id}-${locale.value}`,
  () => $fetch<ProgrammesResponse>('/api/payload-gql', {
    method: 'POST',
    body: {
      query: ManyProgrammesByTypeQuery,
      variables: {
        typeId: typeIds.value.length === 1 ? typeIds.value[0] : undefined,
        locale: locale.value,
      },
    },
  })
)

// Programmes with no page content have nothing to link to, and artist talks
// drop off once their last recurrence has ended -- same rules as the search
// index (see payload.config.ts's shouldSkipSearchSync / beforeSync).
const programmes = computed(() => {
  const now = Date.now()
  return (data.value?.docs ?? []).filter((programme) => {
    if (!programme.content?.length) return false

    if (programme.type?.type === 'artist-talk') {
      const lastEnd = Math.max(
        ...programme.recurrences.map(r => new Date(r.dateTimeEnd).getTime())
      )
      if (!Number.isFinite(lastEnd) || lastEnd < now) return false
    }

    return true
  })
})
</script>

<template>
  <section class="container-d my-3" :class="{ 'px-1': !noHorizontalPadding }">
    <core-rich-text
      v-if="hasRichTextContent(block.richText)"
      :content="block.richText"
      class="mb-2"
    />

    <ul v-if="programmes.length">
      <li
        v-for="programme in programmes"
        :key="programme.id"
        class="list-sep border-current"
      >
        <site-link
          :route-object="{
            name: 'programme-type-slug',
            params: { type: programme.type.slug, slug: programme.slug },
          }"
          class="flex flex-col text-d group"
        >
          <!-- FIXME add types -->
          <!-- {{ programme.types }} -->
          <div class="group-hover:text-accent">
            {{ programme.title }}
          </div>

          <div
            v-if="programme.recurrences?.length"
            class="text-sm"
          >
            <span
              v-for="(recurrence, index) in getFormattedRecurrences(programme.recurrences)"
              :key="index"
              class="block"
            >
              {{ recurrence }}
            </span>
          </div>
  
        </site-link>
      </li>
    </ul>
  </section>
</template>
